import { useEffect, useState } from 'react';
import { GenericPopup, ButtonTheme, GenericAlertPopup } from '@apply/components';
import { isMobileScreen } from '@apply/ui';
import { ProgressSpinner } from '@apply/components';
import { AuthorizedDrawUserDto, ApplyApi } from '@apply/api/src/api/ApplyApi';
import { applicationInsights } from '../../../../../../../ApplicationInsights';
import { DrawUserRadioPanelGroup } from './components/DrawUserRadioPanelGroup';
import './../../../../../../scss/_selectDrawUserModal.scss';

export interface SelectDrawUserModalProps {
    propertyId: number;
    userId?: string;
    isOpen: boolean;
    isEdit?: boolean;
    onClose: () => void;
    onSaved?: () => void;
}

export const SelectDrawUserModal: React.FC<SelectDrawUserModalProps> = ({
    propertyId,
    userId,
    isOpen,
    isEdit = false,
    onClose,
    onSaved,
}) => {
    const mobileScreen = isMobileScreen();
    const [submitting, set_submitting] = useState<boolean>(false);
    const [loading, set_loading] = useState<boolean>(true);
    const [users, setUsers] = useState<AuthorizedDrawUserDto[]>([]);
    const [drawUserSelections, set_drawUserSelections] = useState<string[]>(userId ? [userId] : []);

    useEffect(() => {
        // Only fetch users when modal is actually opened
        if (!isOpen) {
            return;
        }

        const fetchUsers = async () => {
            set_loading(true);
            try {
                const applyApi = new ApplyApi(applicationInsights);
                const response = await applyApi.getDrawAuthorizedUsers();

                if (response.statusCode === 200 && response.result) {
                    setUsers(response.result);
                } else {
                    const errorMsg = response.errors?.length > 0 ? response.errors.join(', ') : 'Failed to load users';
                    throw new Error(errorMsg);
                }
            } catch (error) {
                console.error('Error fetching authorized draw users:', error);
                set_loading(false);
                onClose();
                setTimeout(() => {
                    GenericAlertPopup({
                        title: 'Error',
                        children: 'Failed to load users. Please try again.',
                        width: '350px',
                    });
                }, 100);
                return;
            } finally {
                set_loading(false);
            }
        };

        fetchUsers();
    }, [isOpen, onClose]);

    const handleClose = () => {
        onClose();
    };

    const handleSavePropertyGCAssociation = async () => {
        if (!propertyId) {
            GenericAlertPopup({ title: 'Error', children: 'Property is required' });
            return;
        }

        set_submitting(true);

        try {
            const applyApi = new ApplyApi(applicationInsights);
            const selectedDrawUserId = drawUserSelections[0];

            if (selectedDrawUserId) {
                // Link Property to Draw Authorized User
                const response = await applyApi.updatePropertyDrawAuthorizedUser({
                    propertyId: propertyId,
                    userId: selectedDrawUserId,
                });

                if (response.statusCode === 200) {
                    GenericAlertPopup({
                        title: 'Success',
                        children: 'User added successfully!',
                        width: '350px',
                    });
                    if (onSaved) {
                        onSaved();
                    }
                    handleClose();
                } else {
                    const errorMsg = response.errors?.length > 0 ? response.errors.join(', ') : 'Failed to add user';
                    throw new Error(errorMsg);
                }
            }
        } catch (error) {
            console.error('Failed to save user:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to save user. Please try again.';
            GenericAlertPopup({
                title: 'Error',
                children: errorMessage,
                width: '350px',
            });
        } finally {
            set_submitting(false);
        }
    };

    const actionButtons = [
        {
            id: 'cancelButton',
            theme: ButtonTheme.LinkBlue,
            disabled: submitting,
            children: 'Cancel',
            width: '100px',
            handleClick: handleClose,
        },
        {
            id: 'saveGCPropertyAssociationButton',
            theme: ButtonTheme.Primary,
            children: submitting ? 'Adding...' : 'Add',
            width: '160px',
            disabled: submitting || drawUserSelections.length == 0,
            handleClick: handleSavePropertyGCAssociation,
        },
    ];

    const buildTitle = () => {
        return (
            <div className='selectDrawUserModal-title'>
                <div className='selectDrawUserModal-title title'>{isEdit ? 'Change User' : 'Add User'}</div>
                <div className='selectDrawUserModal-title subheader'>Added user may request draws</div>
            </div>
        );
    };

    return (
        <>
            <GenericPopup
                id='selectDrawUserModal'
                isOpen={isOpen}
                actionButtons={actionButtons}
                handleClose={handleClose}
                aria-labelledby='alert-dialog-title'
                hideOnOutsideClick={true}
                showCloseButton={true}
                showTitle={true}
                title={buildTitle()}
                width='500px'
                height={mobileScreen ? '99%' : '600px'}
                maxWidth='99%'
                maxHeight={mobileScreen ? '99%' : '600px'}
                buttonAlignment='center'
                wrapTitle={true}
                useCustomTitleRender={true}
            >
                <div className={`selectDrawUserModal ${mobileScreen ? 'mobile' : ''}`}>
                    {loading ? (
                        <div>
                            <ProgressSpinner />
                        </div>
                    ) : (
                        <>
                            {users && (
                                <div style={{ height: mobileScreen ? '63vh' : '420px' }}>
                                    <DrawUserRadioPanelGroup
                                        selectedUserIds={drawUserSelections}
                                        setSelectedUserIds={set_drawUserSelections}
                                        users={users}
                                    />
                                </div>
                            )}
                            {!users && 'No Users Available'}
                        </>
                    )}
                </div>
            </GenericPopup>
        </>
    );
};
